import * as React from "react";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Paper,
  Stack,
  Typography,
  alpha,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { ArrowBack, Refresh as RefreshIcon, MyLocation } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import AppNavbar from "../../../components/AppNavbar";
import Header from "../../../components/Header";
import SideMenu from "../../../components/SideMenu";
import AppTheme from "../../../shared-theme/AppTheme";
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from "../../dashboard/theme/customizations";

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const LIVE_REFRESH_MS = 30 * 1000;
const DEFAULT_CENTER = [18.4861, -69.9312]; // Santo Domingo
const DEFAULT_ZOOM = 12;

const makeMarkerIcon = (color, isStale) =>
  L.divIcon({
    className: "",
    html: `
      <div style="
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: ${color};
        border: 3px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        opacity: ${isStale ? 0.55 : 1};
      "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

const COLORS = ["#18586b", "#e91e63", "#ff9800", "#4caf50", "#9c27b0", "#3f51b5", "#00bcd4"];
const colorFor = (idx) => COLORS[idx % COLORS.length];

function FlyToLocation({ position }) {
  const map = useMap();
  React.useEffect(() => {
    if (position) map.flyTo(position, 15, { duration: 0.8 });
  }, [position, map]);
  return null;
}

export default function MapaCobradores(props) {
  const navigate = useNavigate();
  const [cobradores, setCobradores] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [fetchError, setFetchError] = React.useState(null);
  const [selectedId, setSelectedId] = React.useState(null);
  const [trail, setTrail] = React.useState([]);
  const [trailLoading, setTrailLoading] = React.useState(false);
  const [flyTo, setFlyTo] = React.useState(null);

  const fetchLive = React.useCallback(async () => {
    const idcompany = localStorage.getItem("idcompany");
    if (!idcompany) {
      setFetchError("No se encontró idcompany en localStorage. ¿Sesión válida?");
      return;
    }
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/cobradores/${idcompany}/ubicaciones/live`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Usuario-ID": localStorage.getItem("id"),
            "X-Company-ID": idcompany,
          },
        }
      );
      if (!res.ok) {
        const body = await res.text();
        setFetchError(`HTTP ${res.status} — ${body.slice(0, 200)}`);
        setCobradores([]);
        return;
      }
      const data = await res.json();
      setCobradores(Array.isArray(data) ? data : []);
      setFetchError(null);
    } catch (error) {
      console.error("Error fetching ubicaciones live:", error);
      setFetchError(error.message || "Error desconocido");
    }
  }, []);

  const fetchTrail = React.useCallback(async (cobradorId) => {
    if (!cobradorId) {
      setTrail([]);
      return;
    }
    setTrailLoading(true);
    try {
      const fecha = dayjs().format("YYYY-MM-DD");
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/cobradores/${cobradorId}/ubicaciones?fecha=${fecha}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Usuario-ID": localStorage.getItem("id"),
            "X-company-ID": localStorage.getItem("idcompany"),
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        setTrail(Array.isArray(data) ? data : []);
      } else {
        setTrail([]);
      }
    } catch (error) {
      console.error("Error fetching trail:", error);
      setTrail([]);
    } finally {
      setTrailLoading(false);
    }
  }, []);

  React.useEffect(() => {
    setLoading(true);
    fetchLive().finally(() => setLoading(false));
    const interval = setInterval(fetchLive, LIVE_REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchLive]);

  React.useEffect(() => {
    if (selectedId) fetchTrail(selectedId);
    else setTrail([]);
  }, [selectedId, fetchTrail]);

  const withLocation = cobradores.filter((c) => c.lat != null && c.lng != null);
  const selectedCobrador = cobradores.find((c) => c.id_cobrador === selectedId) || null;

  const isStale = (recordedAt) => {
    if (!recordedAt) return true;
    return dayjs().diff(dayjs(recordedAt), "minute") > 10;
  };

  const handleSelectCobrador = (cobrador) => {
    setSelectedId(cobrador.id_cobrador);
    if (cobrador.lat != null && cobrador.lng != null) {
      setFlyTo([cobrador.lat, cobrador.lng]);
    }
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: "flex" }}>
        <SideMenu />
        <AppNavbar />
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: "auto",
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: "center",
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <Header parent={"Gestión Comercial"} child={"Cobros Móviles"} />
            <Container maxWidth={false} sx={{ px: 5, py: 5 }}>
              <Box maxWidth="xl" mx="auto" py={2}>
                <Box display="flex" px={2} mb={2} alignItems="center">
                  <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
                    Atrás
                  </Button>
                  <Box display="flex" flexDirection="column" gap={2} ml={5} flex={1}>
                    <Typography variant="h4" fontWeight={700} textAlign="left">
                      Mapa de Cobradores
                    </Typography>
                    <Typography variant="body1" color="text.secondary" textAlign="left" sx={{ mt: -1 }}>
                      Ubicación en vivo y recorrido del día de los cobradores
                    </Typography>
                  </Box>
                  <Tooltip title="Actualizar">
                    <IconButton onClick={fetchLive} disabled={loading}>
                      {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
                    gap: 2,
                  }}
                >
                  {/* Lista de cobradores */}
                  <Paper sx={{ overflow: "hidden" }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Cobradores activos
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {withLocation.length} con ubicación · {cobradores.length - withLocation.length} sin
                        reporte
                      </Typography>
                    </Box>
                    <List dense sx={{ maxHeight: 560, overflow: "auto", p: 0 }}>
                      {fetchError && (
                        <Box p={2} sx={{ bgcolor: "error.50", borderBottom: 1, borderColor: "error.light" }}>
                          <Typography variant="caption" color="error.main" fontWeight={600} display="block">
                            Error cargando cobradores
                          </Typography>
                          <Typography variant="caption" color="error.main" sx={{ wordBreak: "break-word" }}>
                            {fetchError}
                          </Typography>
                        </Box>
                      )}
                      {cobradores.length === 0 && !loading && !fetchError && (
                        <Box p={3}>
                          <Typography variant="body2" color="text.secondary">
                            No hay cobradores activos.
                          </Typography>
                        </Box>
                      )}
                      {cobradores.map((c, idx) => {
                        const hasLoc = c.lat != null && c.lng != null;
                        const stale = isStale(c.recorded_at);
                        return (
                          <React.Fragment key={c.id_cobrador}>
                            <ListItemButton
                              selected={selectedId === c.id_cobrador}
                              onClick={() => handleSelectCobrador(c)}
                              disabled={!hasLoc}
                            >
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: colorFor(idx), width: 32, height: 32 }}>
                                  {(c.nombre || "?").charAt(0).toUpperCase()}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={c.nombre}
                                secondary={
                                  hasLoc
                                    ? `Hace ${dayjs(c.recorded_at).fromNow?.() || dayjs().diff(dayjs(c.recorded_at), "minute") + " min"}`
                                    : "Sin reporte"
                                }
                                primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                                secondaryTypographyProps={{ fontSize: 12 }}
                              />
                              {hasLoc && (
                                <Chip
                                  size="small"
                                  label={stale ? "Inactivo" : "En línea"}
                                  color={stale ? "default" : "success"}
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </ListItemButton>
                            <Divider component="li" />
                          </React.Fragment>
                        );
                      })}
                    </List>
                  </Paper>

                  {/* Mapa */}
                  <Paper sx={{ overflow: "hidden", height: 620, position: "relative" }}>
                    <MapContainer
                      center={
                        withLocation[0]
                          ? [withLocation[0].lat, withLocation[0].lng]
                          : DEFAULT_CENTER
                      }
                      zoom={DEFAULT_ZOOM}
                      style={{ height: "100%", width: "100%" }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />

                      {withLocation.map((c, idx) => (
                        <Marker
                          key={c.id_cobrador}
                          position={[c.lat, c.lng]}
                          icon={makeMarkerIcon(colorFor(idx), isStale(c.recorded_at))}
                          eventHandlers={{
                            click: () => setSelectedId(c.id_cobrador),
                          }}
                        >
                          <Popup>
                            <Box sx={{ minWidth: 180 }}>
                              <Typography variant="subtitle2" fontWeight={700}>
                                {c.nombre}
                              </Typography>
                              {c.telefono && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {c.telefono}
                                </Typography>
                              )}
                              <Typography variant="caption" display="block" color="text.secondary">
                                Última señal:{" "}
                                {c.recorded_at ? dayjs(c.recorded_at).format("HH:mm") : "—"}
                              </Typography>
                              {c.accuracy != null && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Precisión: {Math.round(c.accuracy)} m
                                </Typography>
                              )}
                            </Box>
                          </Popup>
                        </Marker>
                      ))}

                      {selectedCobrador && trail.length > 1 && (
                        <Polyline
                          positions={trail.map((p) => [p.lat, p.lng])}
                          pathOptions={{
                            color:
                              colorFor(
                                withLocation.findIndex(
                                  (c) => c.id_cobrador === selectedCobrador.id_cobrador
                                )
                              ) || "#18586b",
                            weight: 4,
                            opacity: 0.8,
                          }}
                        />
                      )}

                      <FlyToLocation position={flyTo} />
                    </MapContainer>

                    {selectedCobrador && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          bgcolor: "rgba(255,255,255,0.95)",
                          borderRadius: 2,
                          p: 1.5,
                          boxShadow: 3,
                          zIndex: 500,
                          minWidth: 220,
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <MyLocation fontSize="small" />
                          <Typography variant="subtitle2" fontWeight={700}>
                            {selectedCobrador.nombre}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Recorrido de hoy:{" "}
                          {trailLoading ? "cargando..." : `${trail.length} puntos`}
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => setSelectedId(null)}
                          sx={{ mt: 0.5, px: 0 }}
                        >
                          Limpiar selección
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Box>
              </Box>
            </Container>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
